// components/AddCurrentCustomerRecordModal.jsx

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Grid,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel
} from '@material-ui/core';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import Calendar from "react-modern-calendar-datepicker";
import "react-modern-calendar-datepicker/lib/DatePicker.css";

const rtlTheme = createTheme({
    direction: 'rtl',
    typography: {
        fontFamily: 'IRANSans, Tahoma, sans-serif',
    },
    components: {
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    right: 0,
                    left: 'auto',
                    transformOrigin: 'top right',
                },
                shrink: {
                    transformOrigin: 'top right',
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                input: {
                    textAlign: 'right',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    textAlign: 'right',
                },
                icon: {
                    left: 7,
                    right: 'auto',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    textAlign: 'right',
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    width: '100%',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    textAlign: 'center',
                },
            },
        },
    },
});

const AddCurrentCustomerRecordModal = ({ open, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        startDate: null,
        endDate: null,
        insuranceType: '',
        plate: {
            firstNumber: '',
            letter: '',
            secondNumber: '',
            city: ''
        },
        fullName: '',
        phone: '',
        nationalCode: '',
        motorNumber: '',
        yearOfManufacture: '',
        carName: '',
        chassisNumber: '',
        insuranceNumber: '',
        noClaimMain: '',
        noClaimSub: '',
        financialDiscount: '',
        lifeDiscount: '',
        driverDiscount: ''
    });

    const handleDateChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handlePlateChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            plate: { ...prev.plate, [field]: value }
        }));
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        // Format plate
        const plateString = `${formData.plate.firstNumber} ${formData.plate.letter} ${formData.plate.secondNumber} ${formData.plate.city}`;

        onSubmit({
            ...formData,
            plate: plateString,
            startDate: formData.startDate ? `${formData.startDate.year}/${formData.startDate.month}/${formData.startDate.day}` : null,
            endDate: formData.endDate ? `${formData.endDate.year}/${formData.endDate.month}/${formData.endDate.day}` : null
        });

        // Reset form
        setFormData({
            startDate: null,
            endDate: null,
            insuranceType: '',
            plate: { firstNumber: '', letter: '', secondNumber: '', city: '' },
            fullName: '',
            phone: '',
            nationalCode: '',
            motorNumber: '',
            yearOfManufacture: '',
            carName: '',
            chassisNumber: '',
            insuranceNumber: '',
            noClaimMain: '',
            noClaimSub: '',
            financialDiscount: '',
            lifeDiscount: '',
            driverDiscount: ''
        });
    };

    // چک کردن اینکه نوع بیمه ثالث است یا بدنه
    const isThirdParty = formData.insuranceType === 'ثالث';
    const isBody = formData.insuranceType === 'بدنه';

    return (
        <ThemeProvider theme={rtlTheme}>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    افزودن بیمه‌نامه جدید
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={3} style={{ marginTop: 8 }}>

                        {/* تاریخ پایان */}
                        <Grid item xs={12} md={6}>
                            <Calendar
                                renderInput={({ ref }) => (
                                    <TextField
                                        fullWidth
                                        inputRef={ref}
                                        variant="outlined"
                                        value={formData.endDate ? `${formData.endDate.year}/${String(formData.endDate.month).padStart(2, '0')}/${String(formData.endDate.day).padStart(2, '0')}` : ''}
                                        placeholder="انتخاب تاریخ پایان"
                                        InputProps={{ style: { textAlign: 'right' } }}
                                    />
                                )}
                                value={formData.endDate}
                                onChange={(val) => handleDateChange('endDate', val)}
                                locale="fa"
                                shouldHighlightWeekends
                            />
                        </Grid>

                        {/* نوع بیمه */}
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel>نوع بیمه</InputLabel>
                                <Select
                                    value={formData.insuranceType}
                                    onChange={(e) => handleChange('insuranceType', e.target.value)}
                                    label="نوع بیمه"
                                >
                                    <MenuItem value="">انتخاب کنید</MenuItem>
                                    <MenuItem value="بدنه">بدنه</MenuItem>
                                    <MenuItem value="ثالث">شخص ثالث</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* کد ملی */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="کد ملی"
                                value={formData.nationalCode}
                                onChange={(e) => handleChange('nationalCode', e.target.value)}
                                variant="outlined"
                                inputProps={{ maxLength: 10, style: { textAlign: 'left' } }}
                                InputLabelProps={{ style: { left: 'auto' } }}
                            />
                        </Grid>

                        {/* نام و نام خانوادگی */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="نام و نام خانوادگی"
                                value={formData.fullName}
                                onChange={(e) => handleChange('fullName', e.target.value)}
                                variant="outlined"
                                InputProps={{ style: { textAlign: 'right' } }}
                                InputLabelProps={{ style: { left: 'auto' } }}
                            />
                        </Grid>

                        {/* شماره موبایل */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="شماره موبایل"
                                value={formData.phone}
                                onChange={(e) => handleChange('phone', e.target.value)}
                                variant="outlined"
                                inputProps={{ maxLength: 11, style: { textAlign: 'left' } }}
                                InputLabelProps={{ style: { left: 'auto' } }}
                            />
                        </Grid>

                        {/* پلاک ماشین */}
                        <Grid item xs={12}>
                            <Typography variant="body2" style={{ marginBottom: 8, textAlign: 'right' }}>
                                پلاک ماشین
                            </Typography>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs={3}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="عدد"
                                        value={formData.plate.firstNumber}
                                        onChange={(e) => handlePlateChange('firstNumber', e.target.value)}
                                        inputProps={{ maxLength: 2, style: { textAlign: 'center' } }}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="حرف"
                                        value={formData.plate.letter}
                                        onChange={(e) => handlePlateChange('letter', e.target.value)}
                                        inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="عدد"
                                        value={formData.plate.secondNumber}
                                        onChange={(e) => handlePlateChange('secondNumber', e.target.value)}
                                        inputProps={{ maxLength: 3, style: { textAlign: 'center' } }}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="ایران / شهر"
                                        value={formData.plate.city}
                                        onChange={(e) => handlePlateChange('city', e.target.value)}
                                        inputProps={{ style: { textAlign: 'center' } }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* نام خودرو */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="نام خودرو"
                                value={formData.carName}
                                onChange={(e) => handleChange('carName', e.target.value)}
                                variant="outlined"
                                InputLabelProps={{ style: { left: 'auto' } }}
                            />
                        </Grid>

                        {/* سال خودرو */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="سال خودرو"
                                value={formData.yearOfManufacture}
                                onChange={(e) => handleChange('yearOfManufacture', e.target.value)}
                                variant="outlined"
                                InputLabelProps={{ style: { left: 'auto' } }}
                            />
                        </Grid>

                        {/* شماره موتور */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="شماره موتور"
                                value={formData.motorNumber}
                                onChange={(e) => handleChange('motorNumber', e.target.value)}
                                variant="outlined"
                                InputLabelProps={{ style: { left: 'auto' } }}
                            />
                        </Grid>

                        {/* شماره شاسی */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="شماره شاسی"
                                value={formData.chassisNumber}
                                onChange={(e) => handleChange('chassisNumber', e.target.value)}
                                variant="outlined"
                                InputLabelProps={{ style: { left: 'auto' } }}
                            />
                        </Grid>

                        {/* شماره بیمه‌نامه */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="شماره بیمه‌نامه"
                                value={formData.insuranceNumber}
                                onChange={(e) => handleChange('insuranceNumber', e.target.value)}
                                variant="outlined"
                                InputLabelProps={{ style: { left: 'auto' } }}
                            />
                        </Grid>

                        {/* ========== فیلدهای مربوط به بیمه بدنه ========== */}
                        {isBody && (
                            <>
                                {/* عدم خسارت اصلی */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="عدم خسارت اصلی"
                                        value={formData.noClaimMain}
                                        onChange={(e) => handleChange('noClaimMain', e.target.value)}
                                        variant="outlined"
                                        InputProps={{ style: { textAlign: 'right' } }}
                                        InputLabelProps={{ style: { left: 'auto' } }}
                                    />
                                </Grid>

                                {/* عدم خسارت فرعی */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="عدم خسارت فرعی"
                                        value={formData.noClaimSub}
                                        onChange={(e) => handleChange('noClaimSub', e.target.value)}
                                        variant="outlined"
                                        InputProps={{ style: { textAlign: 'right' } }}
                                        InputLabelProps={{ style: { left: 'auto' } }}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* ========== ۳ فیلد تخفیف‌ها (فقط برای بیمه ثالث) ========== */}
                        {isThirdParty && (
                            <>
                                {/* تخفیف مالی */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="تخفیف مالی"
                                        value={formData.financialDiscount}
                                        onChange={(e) => handleChange('financialDiscount', e.target.value)}
                                        variant="outlined"
                                        InputProps={{ style: { textAlign: 'right' } }}
                                        InputLabelProps={{ style: { left: 'auto' } }}
                                    />
                                </Grid>

                                {/* تخفیف جانی */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="تخفیف جانی"
                                        value={formData.lifeDiscount}
                                        onChange={(e) => handleChange('lifeDiscount', e.target.value)}
                                        variant="outlined"
                                        InputProps={{ style: { textAlign: 'right' } }}
                                        InputLabelProps={{ style: { left: 'auto' } }}
                                    />
                                </Grid>

                                {/* تخفیف راننده */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="تخفیف راننده"
                                        value={formData.driverDiscount}
                                        onChange={(e) => handleChange('driverDiscount', e.target.value)}
                                        variant="outlined"
                                        InputProps={{ style: { textAlign: 'right' } }}
                                        InputLabelProps={{ style: { left: 'auto' } }}
                                    />
                                </Grid>
                            </>
                        )}

                    </Grid>
                </DialogContent>
                <DialogActions style={{ padding: '16px 24px' }}>
                    <Button onClick={onClose} color="default" variant="outlined">
                        انصراف
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained">
                        ثبت رکورد
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
};

export default AddCurrentCustomerRecordModal;